import type { ErrorKind } from '../error-kind'
import type { IntentResult } from '../intent'
import type { AccessOwner } from './access-owner'
import type { AccessResolved } from './resolve-access'
import { parseAccessUrl } from './url'

/** What opening a path needs from the router. */
export interface AccessNavigator {
    pathname: () => string
    push: (path: string) => void
}

/** True when a resolution answers the context the path names, so a newer resolution's answer is never taken for this one. */
const answersPath = (resolution: AccessResolved, path: string): boolean => {
    const url = parseAccessUrl(path)
    switch (url.kind) {
        case 'domain':
            return resolution.context.kind === 'domain' && resolution.context.domainId === url.domainId
        case 'org':
            return resolution.context.kind === 'org' && resolution.context.orgId === url.orgId
        case 'none':
            return true
    }
}

/**
 * Opens a path that names a domain or organization: the access owner resolves it first and the URL
 * is pushed only when it resolved (the URL sync then asks for the same key and gets the same answer
 * without a second request).
 *
 * The result is the intent's only channel back. When the path cannot be opened nothing navigates,
 * the user stays on the page they were on with the domain they had open, and the result
 * carries the kind. A resolution that a newer navigation superseded is neither a success nor a
 * failure: the result says `superseded` and the newer navigation reports its own outcome.
 */
export async function openAccessPath(
    owner: Pick<AccessOwner, 'resolveUrl'>,
    nav: AccessNavigator,
    path: string,
): Promise<IntentResult<AccessResolved>> {
    const startedAt = nav.pathname()
    // The target resolves before the URL moves. A failed attempt leaves the owner as it was, so the
    // page on screen is never replaced by the unavailable state and never remounted.
    const result = await owner.resolveUrl(path, { keepOnFailure: true })
    switch (result.status) {
        case 'resolved':
            break
        case 'idle':
        case 'resolving':
            return { ok: false, superseded: true }
        case 'unavailable':
            return { ok: false, kind: result.reason }
        case 'failed':
            return { ok: false, kind: result.kind, error: result.error }
    }
    if (!answersPath(result, path)) {
        return { ok: false, superseded: true }
    }

    // Any navigation made while the target resolved is newer than this call, whether or not it names a
    // domain, so it wins. The owner now holds the target, so it resolves what the URL shows again; a URL
    // that names nothing is answered by the page the call started on.
    const current = nav.pathname()
    if (current !== startedAt) {
        void owner.resolveUrl(parseAccessUrl(current).kind === 'none' ? startedAt : current)
        return { ok: false, superseded: true }
    }

    if (path !== current) {
        nav.push(path)
    }
    return { ok: true, value: result }
}

/** Asks the owner to resolve the URL again, for the failed state's Retry. Only this folder writes the owner. */
export const retryAccess = (owner: Pick<AccessOwner, 'retry'>): void => {
    void owner.retry()
}

/** What to tell the user when a domain or organization could not be opened. It never says which of "missing" and "refused" it was. */
export function accessSwitchFailureMessage(kind: ErrorKind): string {
    switch (kind) {
        case 'not_found':
        case 'forbidden':
            return 'That domain is not available. It may not exist, or you may not have access to it.'
        case 'misconfigured':
        case 'invalid':
            return 'That domain could not be opened because of a problem we cannot fix by retrying. Contact support if it continues.'
        case 'transient':
        case 'unauthenticated':
        case 'aborted':
            return 'That domain could not be opened right now. Check your connection and try again.'
    }
}
